import { AgencyCampaignSection, AgencyEditorMode } from '../../../../agency/model';
import { Link } from '@tanstack/react-router';
import {
  DashboardDataTableColumnKind,
  buildDashboardDataTableColumns,
  dashboardTableTextActionClassName,
  type DashboardDataTableColumnDefinition,
} from '@shigo-ui/dashboard-ui';
import { Button } from '@shigo-ui/ui';

import { formatDisplayDate } from '../../../../../lib/format';
import { dashboardT } from '../../../../../lib/messages';
import {
  CreativeTab,
  formatCreativeDuration,
  resolveCreativeStatusLabel,
  resolveCreativeStatusTone,
  type Creative,
} from '../../model';
import { CreativeActions } from '../CreativeActions';
import { CreativePreviewCell } from '../CreativePreviewCell';

export function buildCreativesTableColumns({
  agencyId,
  tab,
  brandId,
  duplicatingIds,
  onPreview,
  onVariants,
  onDuplicate,
}: {
  agencyId?: string | undefined;
  tab: CreativeTab;
  brandId: string;
  duplicatingIds: ReadonlySet<string>;
  onPreview: (creative: Creative) => void;
  onVariants: (creative: Creative) => void;
  onDuplicate: (creative: Creative) => void;
}) {
  const nameTo =
    tab === CreativeTab.Display
      ? '/campaigns/creatives/display/edit/$creativeId'
      : '/campaigns/creatives/video/edit/$creativeId';
  const columns: DashboardDataTableColumnDefinition<Creative>[] = [
    {
      id: 'preview',
      kind: DashboardDataTableColumnKind.Custom,
      header: dashboardT('creatives.preview'),
      headerClassName: 'w-24 pl-4',
      className: 'w-24 pl-4',
      cell: (creative) => <CreativePreviewCell creative={creative} onPreview={onPreview} />,
    },
    {
      id: 'name',
      kind: DashboardDataTableColumnKind.Custom,
      header: dashboardT('creatives.name'),
      cell: (creative) => (
        <Link
          to={agencyId ? '/a/$agencyId/campaigns/$section' : nameTo}
          params={{
            creativeId: creative.id,
            agencyId: agencyId ?? '',
            section: AgencyCampaignSection.Creatives,
          }}
          search={{
            brand: brandId,
            ...(agencyId
              ? { editor: AgencyEditorMode.Edit, id: creative.id, creativeTab: tab }
              : {}),
          }}
          className={dashboardTableTextActionClassName()}
        >
          {creative.name}
        </Link>
      ),
    },
    {
      id: 'status',
      kind: DashboardDataTableColumnKind.Status,
      header: dashboardT('creatives.status'),
      label: (creative) => resolveCreativeStatusLabel(creative.activeStatus),
      tone: (creative) => resolveCreativeStatusTone(creative.activeStatus),
    },
    {
      id: 'lineItems',
      kind: DashboardDataTableColumnKind.Text,
      header: dashboardT('creatives.lineItems'),
      value: (creative) => creative.lineItemsCnt ?? 0,
    },
  ];
  if (tab === CreativeTab.Display) {
    columns.push(
      {
        id: 'size',
        kind: DashboardDataTableColumnKind.Text,
        header: dashboardT('creatives.size'),
        value: (creative) =>
          creative.width && creative.height
            ? `${String(creative.width)} x ${String(creative.height)}`
            : '-',
      },
      {
        id: 'type',
        kind: DashboardDataTableColumnKind.Text,
        header: dashboardT('creatives.type'),
        value: (creative) => creative.subType ?? '-',
      },
    );
  } else {
    columns.push(
      {
        id: 'variants',
        kind: DashboardDataTableColumnKind.Custom,
        header: dashboardT('creatives.variants'),
        cell: (creative) => (
          <Button
            type="button"
            variant="ghost"
            className="h-8 px-2"
            disabled={!creative.variants.length}
            aria-label={dashboardT('creatives.activeVariants', {
              active: creative.variants.filter((variant) => variant.active).length,
              total: creative.variants.length,
            })}
            onClick={() => {
              onVariants(creative);
            }}
          >
            {creative.variants.length
              ? `${String(creative.variants.filter((variant) => variant.active).length)}/${String(creative.variants.length)}`
              : '0'}
          </Button>
        ),
      },
      {
        id: 'duration',
        kind: DashboardDataTableColumnKind.Text,
        header: dashboardT('creatives.duration'),
        value: (creative) => formatCreativeDuration(creative.duration),
      },
    );
  }
  columns.push(
    {
      id: 'created',
      kind: DashboardDataTableColumnKind.Date,
      header: dashboardT('creatives.createdOn'),
      headerClassName: 'w-28',
      className: 'w-28 whitespace-nowrap',
      value: (creative) => formatDisplayDate(creative.createdOn),
    },
    {
      id: 'actions',
      kind: DashboardDataTableColumnKind.Actions,
      cell: (creative) => (
        <CreativeActions
          creative={creative}
          tab={tab}
          brandId={brandId}
          pending={duplicatingIds.has(creative.id)}
          onDuplicate={() => {
            onDuplicate(creative);
          }}
        />
      ),
    },
  );
  return buildDashboardDataTableColumns<Creative>(columns);
}
